import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Menu, MenuList, MenuItem, IconButton, Drawer, Box } from '@mui/material'

import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined'
import CloseIcon from '@mui/icons-material/Close'

import { feedback } from '../../../core/feedback'
import { useAjaxContext } from '../../../core/ajax'
import { menuStyles } from './menu.styles'
import { getEnv } from '../../../config/env'

const REACT_APP_WP_URL = getEnv('REACT_APP_WP_URL')
const REACT_APP_WP_CONTENT_CREATOR_URL = getEnv('REACT_APP_WP_CONTENT_CREATOR_URL')

/**
 * @typedef {object} MenuItem
 * @property {string} label - Etiqueta mostrada en el elemento del menú.
 * @property {string} [path] - Ruta de navegación interna (react-router).
 * @property {string} [url] - URL externa o especial ('wp', 'wordpress_url').
 * @property {string} [doc] - URL de documentación (abre en nueva pestaña).
 * @property {Array<MenuItem>} [children] - Sub-elementos del menú (para menús desplegables).
 */

/**
 * @typedef {object} MenuComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {Array<MenuItem>} items - Array de elementos del menú.
 * @property {boolean} isMobileWidth - Si es true, muestra el menú en formato móvil (drawer).
 * @property {boolean} mobileView - Si es true, fuerza el modo móvil independientemente del ancho.
 */

/**
 * Componente de menú de navegación con soporte para desktop y móvil.
 *
 * Renderiza un menú horizontal en desktop o un drawer lateral en móvil. Soporta elementos
 * con sub-menús (desplegables), navegación interna (react-router), apertura de URLs externas,
 * y acciones especiales (generación de enlaces WordPress, contenido comercial). Gestiona
 * selección visual del elemento activo según la ruta actual.
 *
 * @component
 * @example
 * const menuItems = [
 *   { label: 'Inicio', path: '/home' },
 *   { label: 'Documentación', doc: 'https://docs.example.com' },
 *   {
 *     label: 'Productos',
 *     children: [
 *       { label: 'Producto 1', path: '/productos/1' },
 *       { label: 'Producto 2', path: '/productos/2' }
 *     ]
 *   }
 * ];
 * <MenuComponent
 *   items={menuItems}
 *   isMobileWidth={false}
 *   mobileView={false}
 * />
 *
 * @param {MenuComponentProps} props - Las props del componente MenuComponent.
 */
export const MenuComponent = withStyles(menuStyles)(({
  classes,
  items,
  isMobileWidth,
  mobileView,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { post } = useAjaxContext()
  const userId = useSelector((state) => state.global.user_id)

  const [drawerOpen, setDrawerOpen] = useState(false)

  const createNewApp = () => {
    return post(`/instalador/generate_one_use_link/${userId}`)
      .then(({ data }) => window.open(`${REACT_APP_WP_URL}?root=2&id=${data.message}`))
      .catch(() => feedback('error', formatMessage({ id: 'pages.applications.feedback.1' })))
  }

  const getWordpressUrlWithToken = () => {
    return new Promise((resolve, reject) => {
      post('/instalador/wp-business-content')
        .then((response) => {
          if (response && response.status === 201) {
            const token = response.data.message
            window.open(`${REACT_APP_WP_CONTENT_CREATOR_URL}/contenido-comercial/?content=${token}`)
          } else {
            feedback('error', 'No autorizado')
          }
        })
        .catch((error) => {
          if (error.response) {
            feedback('error', `Error al obtener el token: ${error.response.status}`)
          }
          reject(error)
        })
    })
  }

  const handleClickMenuItem = (item) => {
    setDrawerOpen(!drawerOpen)
    if (item.doc) {
      window.open(item.doc)
    } else if (item.url) {
      if (item.url === 'wp') createNewApp()
      else if (item.url === 'wordpress_url') getWordpressUrlWithToken()
      else window.open(item.url)
    } else if (item.path) {
      navigate(item.path)
    }
  }

  const toggleDrawer = (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return
    }
    setDrawerOpen(!drawerOpen)
  }

  const MenuItemCollapsable = ({ item }) => {
    const [anchorEl, setAnchorEl] = React.useState(null)

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
      setAnchorEl(null)
    }

    return (
      <>
        <MenuItem
          classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
          selected={item.children.some((item) => item.path === location.pathname)}
          onClick={handleClick}
        >
          {item.label}
        </MenuItem>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {/* div workaround to avoid console error --> https://github.com/mui/material-ui/issues/15903 */}
          <div>
            <MenuItems items={item.children} />
          </div>
        </Menu>
      </>
    )
  }

  const MenuItems = ({ items }) =>
    items.map((item, i) => (
      <MenuItem
        key={i}
        selected={item.path === location.pathname}
        onClick={() => handleClickMenuItem(item)}
        className={classes.menuItem}
      >
        {item.label}
      </MenuItem>
    ))

  return isMobileWidth || mobileView ? (
    <>
      <IconButton onClick={toggleDrawer}>
        <MenuOutlinedIcon className={classes.menuIcon} />
      </IconButton>
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box p={2}>
          <Box mb={1}>
            <IconButton onClick={toggleDrawer}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </Box>
          {items.map((item, i) => (
            <Box key={i}>
              {item.children && item.children.length ? (
                <Box key={i}>
                  <Box key={i} pb={1} fontSize={20}>
                    <MenuItem
                      classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
                      selected={item.children.some((item) => item.path === location.pathname)}
                    >
                      {item.label}
                    </MenuItem>
                  </Box>
                  <Box ml={4}>
                    <MenuItems items={item.children} />
                  </Box>
                </Box>
              ) : (
                <MenuItem
                  key={i}
                  selected={item.path === location.pathname}
                  onClick={() => handleClickMenuItem(item)}
                  classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
                >
                  {item.label}
                </MenuItem>
              )}
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  ) : (
    <MenuList className={classes.horizontal}>
      {items.map((item, i) => (
        <Box key={i}>
          {item.children && item.children.length ? (
            <MenuItemCollapsable item={item} />
          ) : (
            <MenuItem
              key={i}
              selected={item.path === location.pathname}
              onClick={() => handleClickMenuItem(item)}
              classes={{ root: classes.menuItem, selected: classes.selectedMenuItem }}
            >
              {item.label}
            </MenuItem>
          )}
        </Box>
      ))}
    </MenuList>
  )
})
